import { ChangeEvent, forwardRef, KeyboardEvent, MouseEvent, useMemo } from "react";
import classNames from "classnames";
import { twMerge } from "tailwind-merge";

import { Colors } from "@/constants/colors";
import { INPUT_TYPES } from "@/constants/inputTypes";
import { useOverflow } from "@/utils/providers/OverflowProvider";
import { useTheme } from "@/utils/ThemeProvider";

import { ReactComponent as EyeSvg } from "../assets/icons/eye.svg";
import { ReactComponent as EyeCrossedSvg } from "../assets/icons/eyeCrossed.svg";

interface ZingInputProps {
    type: string;
    id: string;
    label: string;
    value?: string;
    isPrivate?: boolean;
    inputMode?: "search" | "text" | "none" | "email" | "tel" | "url" | "numeric" | "decimal";
    className?: string;
    placeholder?: string;
    onToggleVisibility?: (e: MouseEvent<HTMLButtonElement>) => void;
    onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
    onBlur?: (e: ChangeEvent<HTMLInputElement>) => void;
    onFocus?: (e: ChangeEvent<HTMLInputElement>) => void;
    onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
    errorMessage?: string;
    disabled?: boolean;
    [x: string]: any;
}

const Input = forwardRef<HTMLInputElement, ZingInputProps>(
    (
        {
            type,
            id,
            label,
            value,
            isPrivate,
            inputMode,
            placeholder,
            className,
            onToggleVisibility,
            onChange,
            onBlur,
            onFocus,
            errorMessage,
            disabled,
            ...rest
        },
        ref
    ) => {
        const { isLightTheme } = useTheme();
        const Icon = useMemo(
            () => (type === INPUT_TYPES.PASSWORD ? EyeCrossedSvg : EyeSvg),
            [type]
        );

        // need to fix scroll issue in safari with open keyboard
        const { handleBlur: blur, handleFocus: focus } = useOverflow();

        const handleFocus = (e: ChangeEvent<HTMLInputElement>) => {
            focus();
            onFocus && onFocus(e);
        };

        const handleBlur = (e: ChangeEvent<HTMLInputElement>) => {
            blur();
            onBlur && onBlur(e);
        };

        return (
            <div className="flex flex-col gap-2">
                <label htmlFor={id} className="block text-zing-16-22 text-white female:text-black">
                    {label}
                </label>
                <div className="relative">
                    <input
                        ref={ref}
                        id={id}
                        type={type}
                        value={value || ""}
                        inputMode={inputMode}
                        placeholder={placeholder}
                        onChange={onChange}
                        onBlur={handleBlur}
                        onFocus={handleFocus}
                        className={twMerge(
                            classNames(
                                `w-full rounded-2xl border border-zing-gray-200 bg-transparent px-6 py-4 text-zing-16-22 text-white outline-none placeholder:text-zing-gray-400 focus:border-zing-blue-200 female:border-zing-neutral-cool-grey-400 female:bg-transparent female:text-black female:placeholder-zing-neutral-cool-grey-400 female:focus:border-zing-warm-tangerine-400`,
                                className
                            )
                        )}
                        disabled={disabled}
                        {...rest}
                    />
                    {isPrivate && (
                        <button type="button" onClick={onToggleVisibility}>
                            <Icon
                                color={isLightTheme ? Colors.BLACK : Colors.WHITE}
                                className="absolute right-6 top-1/2 -translate-y-2/4"
                            />
                        </button>
                    )}
                </div>
                {!!errorMessage?.length && (
                    <div className="text-zing-12-18 text-zing-orange-200">{errorMessage}</div>
                )}
            </div>
        );
    }
);

export default Input;
